import React from "react";
import { Field } from "react-final-form";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerField.css";
import cs from "date-fns/locale/cs";
import PropTypes from "prop-types";

registerLocale("cs", cs);

// ─── value helpers ────────────────────────────────────────────────────────────
// Formats (compatible with convertDate() in forms):
//   date+time  "YYYY-MM-DDTHH:mm"
//   date only  "YYYY-MM-DD"
//   time only  "HH:mm"

const parseValue = (value) => {
    if (!value) return { dateStr: null, h: null, m: null };
    // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm" — API format (space separator)
    if (value.includes(" ") && value.includes("-")) {
        const [dateStr, timePart] = value.split(" ");
        const [h, m] = timePart.split(":").map(Number);
        return { dateStr, h, m };
    }
    // "YYYY-MM-DDTHH:mm" — component internal format (T separator)
    if (value.includes("T")) {
        const [dateStr, time] = value.split("T");
        const [h, m] = time.split(":").map(Number);
        return { dateStr, h, m };
    }
    // "HH:mm" — time only
    if (value.includes(":") && value.length <= 5) {
        const [h, m] = value.split(":").map(Number);
        return { dateStr: null, h, m };
    }
    // "YYYY-MM-DD" — date only
    return { dateStr: value, h: null, m: null };
};

const dateStrToDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, mo, d] = dateStr.split("-").map(Number);
    return new Date(y, mo - 1, d);
};

const dateToDateStr = (date) => {
    if (!date) return null;
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");
};

const pad = (n) => String(n ?? 0).padStart(2, "0");
const defaultH = (type) => (type === "to" ? 23 : 0);
const defaultM = (type) => (type === "to" ? 59 : 0);

const formatDisplay = (value, showTimeSelect, onlyTime) => {
    const { dateStr, h, m } = parseValue(value);
    if (onlyTime)        return h != null ? `${pad(h)}:${pad(m)}` : null;
    if (!dateStr)        return null;
    const [y, mo, d] = dateStr.split("-");
    const datePart    = `${d}.${mo}.${y}`;
    if (showTimeSelect && h != null) return `${datePart}  ${pad(h)}:${pad(m)}`;
    return datePart;
};

// ─── TimeSelects ─────────────────────────────────────────────────────────────
const HOURS   = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

// nullable=true → shows "--" option; used for optional time-only fields
const TimeSelects = ({ h, m, disabled, onChange, nullable = false }) => {
    const noValue = h == null;
    return (
        <>
            <select
                value={noValue ? "" : h}
                disabled={disabled}
                className="dpf-time-sel"
                onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") { onChange(null, null); return; }
                    onChange(Number(val), noValue ? 0 : (m ?? 0));
                }}
            >
                {nullable && <option value="">--</option>}
                {HOURS.map((i) => <option key={i} value={i}>{pad(i)}</option>)}
            </select>
            <span className="dpf-colon">:</span>
            <select
                value={noValue ? "" : (m ?? 0)}
                disabled={disabled || noValue}
                className="dpf-time-sel"
                onChange={(e) => onChange(h ?? 0, Number(e.target.value))}
            >
                {nullable && noValue && <option value="">--</option>}
                {MINUTES.map((i) => <option key={i} value={i}>{pad(i)}</option>)}
            </select>
        </>
    );
};

// ─── Trigger — identical look to form-input ───────────────────────────────────
// React.forwardRef because DatePicker passes onClick via ref to customInput
const Trigger = React.forwardRef(function Trigger(
    { displayValue, placeholder, disabled, hasError, value, onClear, onClick },
    ref
) {
    const borderCls = hasError ? "border-red-500" : value ? "border-green-500" : "";
    return (
        <div
            ref={ref}
            onClick={!disabled ? onClick : undefined}
            className={`form-input text-sm dpf-trigger ${borderCls} ${disabled ? "dpf-disabled" : ""}`}
        >
            <span className={`dpf-trigger-text ${displayValue ? "" : "placeholder"}`}>
                {displayValue || placeholder || "Vyberte"}
            </span>
            {value && !disabled && (
                <button type="button" className="dpf-clear"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}>
                    ×
                </button>
            )}
        </div>
    );
});

// ─── shared sub-components ────────────────────────────────────────────────────
const Label = ({ label, isMandatory, note }) =>
    label ? (
        <div className="form-label font-bold flex flex-col pt-1 pb-1">
            <span>
                {label + ": "}
                {isMandatory && <sup className="text-legendyColor">*</sup>}
            </span>
            {note && <span className="text-sm font-normal">{note}</span>}
        </div>
    ) : null;

const ErrorMsg = ({ meta, value, validateFun, valueToCompare }) => {
    if (!meta.error || !meta.touched) return null;
    if (validateFun && valueToCompare) {
        const msg = validateFun(value, valueToCompare);
        return msg ? <div className="text-red-500 text-xs mt-1">{msg}</div> : null;
    }
    if (!value) return <div className="text-red-500 text-xs mt-1">Povinné pole</div>;
    return null;
};

// ─── DatePickerField ──────────────────────────────────────────────────────────
const DatePickerField = (props) => {
    const {
        name, label, isMandatory, showTimeSelect, onlyTime, type,
        validateFun, valueToCompare, onChange, onEnter,
        placeholder, disabled, note,
    } = props;

    const commit = (input, str) => {
        input.onChange(str ?? null);
        if (onChange) onChange(name, str ?? null);
    };

    return (
        <Field
            name={name}
            validate={(value) => {
                if (validateFun && valueToCompare) {
                    if (validateFun(value, valueToCompare) !== "") return true;
                }
                if (isMandatory && !value) return true;
            }}
            render={({ meta, input }) => {
                const { dateStr, h, m } = parseValue(input.value);
                const selectedDate  = dateStrToDate(dateStr);
                const hasValue      = !!input.value;
                const hasError      = !!(meta.touched && meta.error);
                const displayValue  = formatDisplay(input.value, showTimeSelect, onlyTime);

                // ── time only: two selects, no calendar ─────────────────────
                if (onlyTime) {
                    // nullable when field is not mandatory — null stays null until user picks
                    const isNullable = !isMandatory;
                    return (
                        <div className="w-full flex flex-col text-sm p-1">
                            <Label label={label} isMandatory={isMandatory} note={note} />
                            <div className="dpf-only-time">
                                <TimeSelects
                                    h={h} m={m} disabled={disabled}
                                    nullable={isNullable}
                                    onChange={(newH, newM) => {
                                        if (newH == null) { commit(input, null); return; }
                                        commit(input, `${pad(newH)}:${pad(newM)}`);
                                    }}
                                />
                                {hasValue && !disabled && (
                                    <button type="button" className="dpf-clear" style={{marginLeft: 4}}
                                        onClick={() => commit(input, null)}>×</button>
                                )}
                            </div>
                            <ErrorMsg meta={meta} value={input.value}
                                validateFun={validateFun} valueToCompare={valueToCompare} />
                        </div>
                    );
                }

                // ── date only ───────────────────────────────────────────────
                if (!showTimeSelect) {
                    return (
                        <div className="w-full flex flex-col text-sm p-1">
                            <Label label={label} isMandatory={isMandatory} note={note} />
                            <DatePicker
                                locale="cs"
                                selected={selectedDate}
                                onChange={(date) => commit(input, dateToDateStr(date))}
                                dateFormat="dd.MM.yyyy"
                                disabled={disabled}
                                calendarClassName="dpf-calendar"
                                popperPlacement="bottom-start"
                                popperProps={{ strategy: "fixed" }}
                                onKeyDown={({ key }) => { if (key === "Enter" && onEnter) onEnter(); }}
                                customInput={
                                    <Trigger
                                        displayValue={displayValue}
                                        placeholder={placeholder}
                                        disabled={disabled}
                                        hasError={hasError}
                                        value={input.value}
                                        onClear={() => commit(input, null)}
                                    />
                                }
                            />
                            <ErrorMsg meta={meta} value={input.value}
                                validateFun={validateFun} valueToCompare={valueToCompare} />
                        </div>
                    );
                }

                // ── date + time: calendar popup with time selects inside ─────
                const curH = h ?? defaultH(type);
                const curM = m ?? defaultM(type);

                const handleDateChange = (date) => {
                    if (!date) { commit(input, null); return; }
                    const ds   = dateToDateStr(date);
                    const useH = hasValue ? curH : defaultH(type);
                    const useM = hasValue ? curM : defaultM(type);
                    commit(input, `${ds}T${pad(useH)}:${pad(useM)}`);
                };

                const handleTimeChange = (newH, newM) => {
                    const ds = dateStr ?? dateToDateStr(new Date());
                    commit(input, `${ds}T${pad(newH)}:${pad(newM)}`);
                };

                return (
                    <div className="w-full flex flex-col text-sm p-1">
                        <Label label={label} isMandatory={isMandatory} note={note} />
                        <DatePicker
                            locale="cs"
                            selected={selectedDate}
                            onChange={handleDateChange}
                            dateFormat="dd.MM.yyyy"
                            disabled={disabled}
                            shouldCloseOnSelect={false}
                            calendarClassName="dpf-calendar"
                            popperPlacement="bottom-start"
                            popperProps={{ strategy: "fixed" }}
                            onKeyDown={({ key }) => { if (key === "Enter" && onEnter) onEnter(); }}
                            customInput={
                                <Trigger
                                    displayValue={displayValue}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                    hasError={hasError}
                                    value={input.value}
                                    onClear={() => commit(input, null)}
                                />
                            }
                        >
                            {/* time selects inside calendar popup */}
                            <div className="dpf-time-strip">
                                <span className="dpf-time-label">Čas:</span>
                                <TimeSelects
                                    h={curH} m={curM} disabled={disabled}
                                    onChange={handleTimeChange}
                                />
                            </div>
                        </DatePicker>
                        <ErrorMsg meta={meta} value={input.value}
                            validateFun={validateFun} valueToCompare={valueToCompare} />
                    </div>
                );
            }}
        />
    );
};

DatePickerField.defaultProps = {
    disabled: false,
    showTimeSelect: false,
    onlyTime: false,
    isMandatory: false,
};

DatePickerField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isMandatory: PropTypes.bool.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    showTimeSelect: PropTypes.bool,
    onlyTime: PropTypes.bool,
    type: PropTypes.string,
    validateFun: PropTypes.func,
    valueToCompare: PropTypes.string,
    onChange: PropTypes.func,
    onEnter: PropTypes.func,
    note: PropTypes.string,
};

export default DatePickerField;
